import { useState, useEffect, useRef } from "react";
import {Link} from "react-router-dom";

const BLUE = "#da291c";
const NAVY = "#0A2A4F";

const services = [
  { id: 1, emoji: "🔐", tag: "Network Security", title: "Next-Gen Firewall", sub: "FortiGate NGFW", desc: "AI-powered threat prevention with deep packet inspection, intrusion prevention, and full SSL inspection — unified in one platform.", features: ["Deep Packet Inspection", "SSL/TLS Inspection", "IPS", "App Control"], accent: "#0080FF" },
  { id: 2, emoji: "🌍", tag: "Unified SASE", title: "Secure SD-WAN", sub: "Zero-Trust Connectivity", desc: "Connect any user to any application from any location — securely and reliably — with integrated SD-WAN and zero-trust access.", features: ["ZTNA", "SD-WAN", "Cloud On-Ramp", "DEM Monitoring"], accent: "#0057B3" },
  { id: 3, emoji: "📊", tag: "Security Operations", title: "SOC Platform", sub: "FortiAnalyzer & SIEM", desc: "Unify threat detection, investigation, and automated response across your entire environment with AI-driven analytics.", features: ["SIEM & SOAR", "EDR / XDR", "NDR", "SOCaaS"], accent: "#00B4D8" },
  { id: 4, emoji: "☁️", tag: "Cloud Security", title: "Cloud Protection", sub: "CNAPP & WAF", desc: "Secure every step of your cloud journey — from code to runtime — with unified visibility across any cloud provider.", features: ["CNAPP", "Cloud NGFW", "API Protection", "WAAP"], accent: "#0080FF" },
  { id: 5, emoji: "🤖", tag: "AI Security", title: "FortiAI Platform", sub: "Predict. Prevent. Respond.", desc: "Purpose-built AI that anticipates threats before they strike, automates SecOps workflows, and secures AI infrastructure itself.", features: ["FortiAI-Protect", "FortiAI-Assist", "FortiAI-Secure", "Agentic AI"], accent: "#7B2FBE" },
  { id: 6, emoji: "🏭", tag: "OT Security", title: "Operational Tech", sub: "ICS / SCADA Defense", desc: "Harden industrial control systems and critical infrastructure with ruggedized hardware and FortiGuard OT threat intelligence.", features: ["Ruggedized HW", "OT IPS", "NAC for OT", "SOC Analytics"], accent: "#0A2A4F" },
];

const stats = [
  { value: 700, suffix: "K+", label: "Enterprises Secured" },
  { value: 50, suffix: "+", label: "Gartner Recognitions" },
  { value: 99, suffix: ".9%", label: "Uptime SLA" },
  { value: 24, suffix: "/7", label: "Expert Support" },
];

const whyUs = [
  { icon: "🏆", title: "Certified Partner", desc: "Advanced Fortinet certifications covering the full Security Fabric — from NGFW to FortiAI." },
  { icon: "📍", title: "Local Expertise", desc: "Regionally based engineers with deep knowledge of compliance needs across MEA." },
  { icon: "⚙️", title: "Full Lifecycle", desc: "Design, deployment, and 24/7 managed monitoring — we own your security outcome." },
  { icon: "📈", title: "Proven Results", desc: "Hundreds of successful deployments across finance, government, and enterprise sectors." },
];

function useInView(threshold = 0.1) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

function useMobile() {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);
  return isMobile;
}

function Counter({ target, suffix }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !started.current) {
        started.current = true;
        let cur = 0;
        const steps = 50;
        const inc = target / steps;
        const timer = setInterval(() => {
          cur += inc;
          if (cur >= target) { setVal(target); clearInterval(timer); }
          else setVal(Math.floor(cur));
        }, 1400 / steps);
      }
    }, { threshold: 0.3 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [target]);
  return <span ref={ref}>{val}{suffix}</span>;
}

const FortinetPage = () => {
  const [active, setActive] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  // const [menuOpen, setMenuOpen] = useState(false);
  const isMobile = useMobile();

  const [heroRef, heroVisible] = useInView(0.1);
  const [statsRef, statsVisible] = useInView(0.1);
  const [servicesRef, servicesVisible] = useInView(0.05);
  const [whyRef, whyVisible] = useInView(0.1);
  const [aiRef, aiVisible] = useInView(0.1);
  const [ctaRef, ctaVisible] = useInView(0.1);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  // const px = isMobile ? "20px" : "48px";
  const sectionPad = isMobile ? "64px 20px" : "100px 48px";

  return (
    <div style={{ fontFamily: '"nbInternational", sans-serif', color: "#0A2A4F", overflowX: "hidden" }}>
      <style>{`
        * { box-sizing: border-box; margin: 0; padding: 0; font-family: "nbInternational", sans-serif; }
        body { font-family: "nbInternational", sans-serif; }

        .btn-blue { background: #0080FF; color: #fff; border: none; padding: 13px 28px; border-radius: 8px; font-family: "nbInternational", sans-serif; font-weight: 600; font-size: 14px; cursor: pointer; transition: all 0.2s; }
        .btn-blue:hover { background: #0066CC; transform: translateY(-1px); box-shadow: 0 6px 20px rgba(0,128,255,0.35); }
        .btn-ghost { background: transparent; color: #0080FF; border: 1.5px solid #0080FF; padding: 12px 28px; border-radius: 8px; font-family: "nbInternational", sans-serif; font-weight: 600; font-size: 14px; cursor: pointer; transition: all 0.2s; }
        .btn-ghost:hover { background: rgba(0,128,255,0.06); }
        .btn-full { width: 100%; justify-content: center; }

        .service-card { background: #fff; border: 1.5px solid #E8EEF6; border-radius: 16px; padding: 24px; cursor: pointer; transition: all 0.25s cubic-bezier(0.4,0,0.2,1); position: relative; overflow: hidden; }
        .service-card::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 3px; background: var(--accent, #0080FF); transform: scaleX(0); transform-origin: left; transition: transform 0.3s ease; border-radius: 16px 16px 0 0; }
        .service-card:hover, .service-card.open { border-color: rgba(0,128,255,0.2); box-shadow: 0 8px 32px rgba(0,128,255,0.1); }
        .service-card:hover::before, .service-card.open::before { transform: scaleX(1); }

        .stat-pill { background: #fff; border: 1.5px solid #E8EEF6; border-radius: 16px; padding: 24px 20px; text-align: center; transition: all 0.25s; }
        .why-card { background: #fff; border: 1.5px solid #E8EEF6; border-radius: 16px; padding: 28px; transition: all 0.25s; }
        .why-card:hover { border-color: rgba(0,128,255,0.2); box-shadow: 0 8px 28px rgba(0,128,255,0.08); }

        .chip { display: inline-flex; align-items: center; gap: 6px; background: rgb(255 17 0); color: white; border-radius: 100px; padding: 5px 14px; font-size: 12px; letter-spacing: 0.5px; }
        .feature-tag { background: #F0F6FF; color: #0057B3; border-radius: 6px; padding: 4px 10px; font-size: 12px; font-weight: 500; }

        .dots-bg { background-image: radial-gradient(circle, rgba(0,128,255,0.12) 1.5px, transparent 1.5px); background-size: 28px 28px; }

        /* Mobile menu */
        .mobile-menu { position: fixed; top: 68px; left: 0; right: 0; background: #fff; border-bottom: 1px solid #E8EEF6; padding: 20px; display: flex; flex-direction: column; gap: 12px; z-index: 99; box-shadow: 0 8px 24px rgba(0,0,0,0.08); }

        /* Responsive grids */
        .hero-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 64px; align-items: center; }
        .stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; }
        .services-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 16px; }
        .ai-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 80px; align-items: center; }
        .why-layout { display: grid; grid-template-columns: 1fr 2fr; gap: 80px; align-items: flex-start; }
        .why-cards-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }

        @media (max-width: 1024px) {
          .hero-grid { grid-template-columns: 1fr; gap: 40px; }
          .ai-grid { grid-template-columns: 1fr; gap: 48px; }
          .why-layout { grid-template-columns: 1fr; gap: 40px; }
          .stats-grid { grid-template-columns: repeat(2, 1fr); }
        }

        @media (max-width: 640px) {
          .stats-grid { grid-template-columns: repeat(2, 1fr); gap: 10px; }
          .services-grid { grid-template-columns: 1fr; }
          .why-cards-grid { grid-template-columns: 1fr; }
          .hero-visual { display: none; }
          .btn-blue, .btn-ghost { width: 100%; text-align: center; }
          .cta-buttons { flex-direction: column; }
        }

        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: #F7F9FC; }
        ::-webkit-scrollbar-thumb { background: #0080FF; border-radius: 4px; }
      `}</style>

      {/* HERO */}
      <section className="" ref={heroRef} style={{ minHeight: isMobile ? "auto" : "100vh-", display: "flex", alignItems: "center", padding: isMobile ? "100px 20px 64px" : "100px 48px 80px", position: "relative", overflow: "hidden", background: "linear-gradient(160deg, #EEF6FF 0%, #F7F9FC 50%, #EAF3FF 100%)" }}>
        <div style={{ position: "absolute", width: 400, height: 400, borderRadius: "50%", filter: "blur(80px)", background: "rgba(0,128,255,0.1)", top: -80, right: -80, pointerEvents: "none" }} />
        <div style={{ position: "absolute", width: 300, height: 300, borderRadius: "50%", filter: "blur(80px)", background: "rgba(10,42,79,0.06)", bottom: -60, left: 60, pointerEvents: "none" }} />
        <div className="dots-bg" style={{ position: "absolute", inset: 0, opacity: 0.5 }} />

        <div className="md:w-theme-container w-mobile-container mx-auto" style={{  margin: "0 auto", position: "relative", zIndex: 2 }}>
          <div className="hero-grid">
            {/* Left text */}
            <div>
              <div className="chip" style={{ marginBottom: 20, opacity: heroVisible ? 1 : 0, transform: heroVisible ? "none" : "translateY(12px)", transition: "all 0.5s ease" }}>
                <span>🔒</span> Official Fortinet Partner
              </div>
              <h1 style={{ fontSize: "clamp(36px, 6vw, 68px)", fontWeight: 800, lineHeight: 1.08, color: NAVY, marginBottom: 20, opacity: heroVisible ? 1 : 0, transform: heroVisible ? "none" : "translateY(20px)", transition: "all 0.6s ease 0.1s" }}>
                Enterprise Security,<br /><span style={{ color: BLUE }}>Simplified</span> &<br />Delivered Locally.
              </h1>
              <p style={{ fontSize: isMobile ? 15 : 17, lineHeight: 1.75, color: "#4A6280", maxWidth: 480, marginBottom: 32, opacity: heroVisible ? 1 : 0, transition: "all 0.6s ease 0.2s" }}>
                Mesoft partners with Fortinet — the global leader in cybersecurity — to deliver next-generation firewalls, AI-driven SOC platforms, and unified SASE solutions for your organization.
              </p>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap", opacity: heroVisible ? 1 : 0, transition: "all 0.6s ease 0.3s" }} className="cta-buttons">
                <Link to={'/contact'} className="btn-blue">Explore Solutions →</Link>
                <Link to={'/contact'} className="btn-ghost">Talk to an Expert</Link>
              </div>
            </div>

            {/* Right card — hidden on mobile via CSS class */}
            <div className="hero-visual" style={{ opacity: heroVisible ? 1 : 0, transform: heroVisible ? "none" : "translateX(30px)", transition: "all 0.8s ease 0.2s" }}>
              <div style={{ background: "#fff", borderRadius: 20, padding: 32, boxShadow: "0 20px 60px rgba(0,128,255,0.12), 0 4px 16px rgba(0,0,0,0.06)", border: "1.5px solid rgba(0,128,255,0.1)", position: "relative" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
                  <div>
                    <div style={{ fontSize: 13, color: "#7A8FA6", fontWeight: 500, marginBottom: 4 }}>Security Posture</div>
                    <div style={{ fontSize: 28, fontWeight: 800, color: NAVY }}>Protected</div>
                  </div>
                  <div style={{ width: 48, height: 48, background: "rgba(0,128,255,0.1)", borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>🛡️</div>
                </div>
                {[{ label: "Threat Prevention", val: 99, color: "#0080FF" }, { label: "Network Coverage", val: 94, color: "#00B4D8" }, { label: "SOC Automation", val: 87, color: "#7B2FBE" }].map(item => (
                  <div key={item.label} style={{ marginBottom: 16 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                      <span style={{ fontSize: 13, color: "#4A6280", fontWeight: 500 }}>{item.label}</span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: item.color }}>{item.val}%</span>
                    </div>
                    <div style={{ height: 6, background: "#EEF3FA", borderRadius: 100, overflow: "hidden" }}>
                      <div style={{ height: "100%", borderRadius: 100, background: item.color, width: heroVisible ? `${item.val}%` : "0%", transition: "width 1.4s cubic-bezier(0.4,0,0.2,1) 0.8s" }} />
                    </div>
                  </div>
                ))}
                <div style={{ position: "absolute", top: -16, right: -20, background: "#fff", borderRadius: 12, padding: "10px 16px", boxShadow: "0 8px 24px rgba(0,0,0,0.1)", border: "1.5px solid #E8EEF6", display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 16 }}>✅</span><span style={{ fontSize: 12, fontWeight: 600, color: NAVY }}>99.9% Uptime</span>
                </div>
                <div style={{ position: "absolute", bottom: -16, left: -20, background: `linear-gradient(135deg, ${BLUE}, ${NAVY})`, borderRadius: 12, padding: "10px 16px", boxShadow: "0 8px 24px rgba(0,128,255,0.3)", display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 16 }}>🤖</span><span style={{ fontSize: 12, fontWeight: 600, color: "#fff" }}>AI-Powered</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section ref={statsRef} style={{ padding: isMobile ? "48px 20px" : "72px 48px", background: "#fff", borderTop: "1px solid #E8EEF6", borderBottom: "1px solid #E8EEF6" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div className="stats-grid">
            {stats.map((s, i) => (
              <div key={i} className="stat-pill" style={{ opacity: statsVisible ? 1 : 0, transform: statsVisible ? "none" : "translateY(20px)", transition: `all 0.55s ease ${i * 0.1}s` }}>
                <div style={{ fontSize: isMobile ? 34 : 44, fontWeight: 800, color: BLUE, lineHeight: 1, marginBottom: 8 }}>
                  {statsVisible ? <Counter target={s.value} suffix={s.suffix} /> : `0${s.suffix}`}
                </div>
                <div style={{ fontSize: 12, color: "#7A8FA6", fontWeight: 500 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-16" ref={servicesRef} style={{ background: "#F7F9FC" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: isMobile ? 40 : 64 }}>
            <div className="chip" style={{ marginBottom: 14, opacity: servicesVisible ? 1 : 0, transition: "all 0.5s" }}>Solutions Portfolio</div>
            <h2 style={{ fontSize: "clamp(28px, 4vw, 52px)", fontWeight: 800, color: NAVY, marginBottom: 14, lineHeight: 1.15, opacity: servicesVisible ? 1 : 0, transform: servicesVisible ? "none" : "translateY(20px)", transition: "all 0.55s ease 0.1s" }}>
              Fortinet Security Fabric,<br /><span style={{ color: BLUE }}>Delivered by Mesoft</span>
            </h2>
            <p style={{ fontSize: 15, color: "#4A6280", maxWidth: 500, margin: "0 auto", lineHeight: 1.7, opacity: servicesVisible ? 1 : 0, transition: "all 0.55s ease 0.2s" }}>
              Every solution integrates natively within the Security Fabric — reducing complexity while your protection.
            </p>
          </div>
          <div className="services-grid">
            {services.map((svc, i) => (
              <div key={svc.id} className={`service-card ${active === svc.id ? "open" : ""} shadow-2xl`}
                   style={{ "--accent": svc.accent, opacity: servicesVisible ? 1 : 0, transform: servicesVisible ? "none" : "translateY(24px)", transition: `opacity 0.55s ease ${i * 0.07}s, transform 0.55s ease ${i * 0.07}s, border-color 0.25s, box-shadow 0.25s` }}
                   onClick={() => setActive(active === svc.id ? null : svc.id)}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
                  <div style={{ width: 48, height: 48, background: `${svc.accent}12`, borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>{svc.emoji}</div>
                  <div className="border border-slate-500" style={{ width: 26, height: 26, borderRadius: 7, border: "1.5px solid #E8EEF6", display: "flex", alignItems: "center", justifyContent: "center", color: active === svc.id ? BLUE : "#B0BFCF", fontSize: 16, transition: "transform 0.3s, color 0.2s", transform: active === svc.id ? "rotate(45deg)" : "none" }}>+</div>
                </div>
                <div style={{ fontSize: 11, fontWeight: 600, color: svc.accent, letterSpacing: 1, textTransform: "uppercase", marginBottom: 5 }}>{svc.tag}</div>
                <h3 style={{ fontSize: 19, fontWeight: 700, color: NAVY, marginBottom: 3 }}>{svc.title}</h3>
                <div style={{ fontSize: 12, color: "#7A8FA6", marginBottom: 10 }}>{svc.sub}</div>
                <p style={{ fontSize: 14, color: "#4A6280", lineHeight: 1.7, marginBottom: 14 }}>{svc.desc}</p>
                <div style={{ maxHeight: active === svc.id ? "200px" : "0", overflow: "hidden", transition: "max-height 0.35s ease" }}>
                  <div style={{ borderTop: "1px solid #EEF3FA", paddingTop: 12, marginBottom: 14 }}>
                    <div style={{ fontSize: 10, color: "#B0BFCF", fontWeight: 600, letterSpacing: 1, marginBottom: 8, textTransform: "uppercase" }}>Key Capabilities</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>{svc.features.map(f => <span key={f} className="feature-tag">{f}</span>)}</div>
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 5, color: svc.accent, fontSize: 13, fontWeight: 600 }}>
                  {active === svc.id ? "Show less" : "Learn more"}
                  <span style={{ transition: "transform 0.25s", transform: active === svc.id ? "rotate(90deg)" : "none", display: "inline-block" }}>→</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FORTIAI */}
      <section ref={aiRef} style={{ padding: sectionPad, background: `linear-gradient(135deg, ${NAVY} 0%, #0D3D73 60%, #0B3060 100%)`, position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", width: 400, height: 400, borderRadius: "50%", border: "1px solid rgba(255,255,255,0.05)", top: -150, right: -80 }} />
        <div style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse at 70% 50%, rgba(0,128,255,0.2) 0%, transparent 60%)" }} />
        <div className="md:w-theme-container w-mobile-container mx-auto" style={{margin: "0 auto", position: "relative", zIndex: 2 }}>
          <div className="ai-grid">
            <div>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,0.08)", color: "#7DD3FC", borderRadius: 100, padding: "5px 14px", fontSize: 12, fontWeight: 600, marginBottom: 20, opacity: aiVisible ? 1 : 0, transition: "all 0.5s" }}>
                <span>🤖</span> FortiAI Platform
              </div>
              <h2 style={{ fontSize: "clamp(28px, 4vw, 52px)", fontWeight: 800, color: "#fff", lineHeight: 1.1, marginBottom: 18, opacity: aiVisible ? 1 : 0, transform: aiVisible ? "none" : "translateY(20px)", transition: "all 0.6s ease 0.1s" }}>
                Security That Thinks<br /><span style={{ color: "#60B0FF" }}>Before You Act.</span>
              </h2>
              <p style={{ fontSize: isMobile ? 14 : 16, color: "rgba(255,255,255,0.6)", lineHeight: 1.75, marginBottom: 28, opacity: aiVisible ? 1 : 0, transition: "all 0.6s ease 0.2s" }}>
                FortiAI shifts your security posture from reactive to predictive. Three purpose-built AI modules work in concert — defending, automating, and securing AI infrastructure itself.
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 14, opacity: aiVisible ? 1 : 0, transition: "all 0.6s ease 0.3s" }}>
                {[{ name: "FortiAI–Protect", desc: "Real-time defense against AI-powered threats", color: "#60B0FF" }, { name: "FortiAI–Assist", desc: "Automates SOC & NOC with agentic AI", color: "#7DD3FC" }, { name: "FortiAI–Secure", desc: "Secures LLM workloads and AI data pipelines", color: "#BAE6FD" }].map(item => (
                  <div key={item.name} style={{ display: "flex", gap: 12, alignItems: "center" }}>
                    <div style={{ width: 38, height: 38, background: "rgba(255,255,255,0.07)", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <div style={{ width: 8, height: 8, background: item.color, borderRadius: "50%" }} />
                    </div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: item.color, marginBottom: 1 }}>{item.name}</div>
                      <div style={{ fontSize: 13, color: "rgba(255,255,255,0.5)" }}>{item.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 20, padding: isMobile ? 24 : 36, backdropFilter: "blur(10px)", opacity: aiVisible ? 1 : 0, transform: aiVisible ? "none" : "translateY(20px)", transition: "all 0.7s ease 0.2s" }}>
              <div style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", marginBottom: 20, letterSpacing: 1 }}>AI PERFORMANCE METRICS</div>
              {[{ label: "Threat Detection Rate", val: 99.7, color: "#60B0FF" }, { label: "False Positive Reduction", val: 87, color: "#7DD3FC" }, { label: "SOC Automation Coverage", val: 94, color: "#BAE6FD" }, { label: "MTTR Improvement", val: 76, color: "#38BDF8" }].map((item, i) => (
                <div key={item.label} style={{ marginBottom: 18 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 7 }}>
                    <span style={{ fontSize: 13, color: "rgba(255,255,255,0.6)", fontWeight: 500 }}>{item.label}</span>
                    <span style={{ fontSize: 13, color: item.color, fontWeight: 700 }}>{item.val}%</span>
                  </div>
                  <div style={{ height: 6, background: "rgba(255,255,255,0.08)", borderRadius: 100, overflow: "hidden" }}>
                    <div style={{ height: "100%", borderRadius: 100, background: item.color, width: aiVisible ? `${item.val}%` : "0%", transition: `width 1.4s cubic-bezier(0.4,0,0.2,1) ${0.4 + i * 0.15}s` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WHY MESOFT */}
      <section className="md:w-theme-container w-mobile-container mx-auto py-20" ref={whyRef} style={{ background: "#fff" }}>
        <div style={{margin: "0 auto" }}>
          <div className="why-layout">
            <div style={{ position: isMobile ? "static" : "sticky", top: 100 }}>
              <div className="chip" style={{ marginBottom: 18, opacity: whyVisible ? 1 : 0, transition: "all 0.5s" }}>Why Mesoft</div>
              <h2 style={{ fontSize: "clamp(28px, 3.5vw, 48px)", fontWeight: 800, color: NAVY, lineHeight: 1.15, marginBottom: 16, opacity: whyVisible ? 1 : 0, transform: whyVisible ? "none" : "translateY(20px)", transition: "all 0.6s ease 0.1s" }}>
                Your Local<br /><span style={{ color: BLUE }}>Security Partner.</span>
              </h2>
              <p style={{ fontSize: 15, color: "#4A6280", lineHeight: 1.7, marginBottom: 28, opacity: whyVisible ? 1 : 0, transition: "all 0.6s ease 0.2s" }}>
                We combine Fortinet's world-class technology with deep regional expertise, delivering tailored cybersecurity outcomes — not just products.
              </p>
              <Link to={'/contact'} className="btn-blue" style={{ opacity: whyVisible ? 1 : 0, transition: "all 0.6s ease 0.3s" }}>Meet Our Team →</Link>
            </div>
            <div className="why-cards-grid">
              {whyUs.map((item, i) => (
                <div key={i} className="why-card shadow-2xl" style={{ opacity: whyVisible ? 1 : 0, transform: whyVisible ? "none" : "translateY(24px)", transition: `all 0.55s ease ${i * 0.1}s` }}>
                  <div style={{ width: 48, height: 48, background: "rgba(0,128,255,0.08)", borderRadius: 13, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, marginBottom: 16 }}>{item.icon}</div>
                  <h3 style={{ fontSize: 17, fontWeight: 700, color: NAVY, marginBottom: 8 }}>{item.title}</h3>
                  <p style={{ fontSize: 14, color: "#4A6280", lineHeight: 1.7 }}>{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section ref={ctaRef} style={{ padding: sectionPad, background: "#EEF6FF" }}>
        <div style={{ maxWidth: 680, margin: "0 auto", textAlign: "center" }}>
          <div style={{ width: 60, height: 60, background: `linear-gradient(135deg, ${BLUE}, ${NAVY})`, borderRadius: 16, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, margin: "0 auto 24px", opacity: ctaVisible ? 1 : 0, transform: ctaVisible ? "none" : "scale(0.8)", transition: "all 0.5s" }}>🛡️</div>
          <h2 style={{ fontSize: "clamp(28px, 4.5vw, 56px)", fontWeight: 800, color: NAVY, lineHeight: 1.1, marginBottom: 16, opacity: ctaVisible ? 1 : 0, transform: ctaVisible ? "none" : "translateY(20px)", transition: "all 0.6s ease 0.1s" }}>
            Ready to Secure<br /><span style={{ color: BLUE }}>Your Infrastructure?</span>
          </h2>
          <p style={{ fontSize: isMobile ? 15 : 17, color: "#4A6280", lineHeight: 1.75, marginBottom: 36, opacity: ctaVisible ? 1 : 0, transition: "all 0.6s ease 0.2s" }}>
            Our certified engineers will serve your environment, identify gaps, and design a security architecture built for your business — at no initial cost.
          </p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap", opacity: ctaVisible ? 1 : 0, transition: "all 0.6s ease 0.3s" }} className="cta-buttons">
            <Link to={'/contact'} className="btn-blue" style={{ fontSize: 15, padding: "13px 28px" }}>Request Free Assessment</Link>
            <Link to={'/contact'} className="btn-ghost" style={{ fontSize: 15, padding: "13px 28px" }}>View All Solutions</Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default FortinetPage
